import React from 'react';
import styles from './Loader.module.css';

interface LoaderProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fullScreen?: boolean;
  className?: string;
}

/**
 * Premium Loader component using the Delt.ai logo with a wave-fill animation.
 * 
 * @param size - The size of the logo ('sm', 'md', 'lg', 'xl'). Defaults to 'md'.
 * @param fullScreen - If true, the loader will cover the entire screen with a glassmorphism backdrop.
 * @param className - Optional additional CSS class for the container.
 */
export const Loader: React.FC<LoaderProps> = ({ 
  size = 'md', 
  fullScreen = false, 
  className = '' 
}) => {
  const containerClasses = [
    styles.container,
    fullScreen ? styles.fullScreen : '',
    className
  ].filter(Boolean).join(' ');

  const svgClasses = [
    styles.svg,
    styles[size]
  ].join(' ');

  // Logo path from logo_delta.svg
  const logoPath = "M27.9999 0C12.5613 0 0 12.5602 0 27.9988C0 43.4398 12.5613 56 27.9999 56C43.4393 56 56 43.4398 56 27.9988C56 12.5602 43.4393 0 27.9999 0ZM3.774 32.0118L26.8533 3.47183C13.8436 4.07228 3.44426 14.8443 3.44426 27.9988C3.44426 29.3652 3.55854 30.7059 3.774 32.0118ZM45.6431 45.0605L27.9999 23.2462L10.3578 45.0608C7.94172 42.5642 6.0505 39.5582 4.86209 36.2213L27.9999 7.53248L51.1795 36.1044C49.9928 39.4879 48.0856 42.5352 45.6431 45.0605ZM12.9219 47.3684L27.9999 28.7232L43.0778 47.3681C38.9133 50.6166 33.679 52.5555 27.9999 52.5555C22.3204 52.5555 17.0858 50.6166 12.9219 47.3684ZM52.5557 27.9988C52.5557 29.3652 52.4412 30.7056 52.2263 32.0118L29.147 3.47183C42.1555 4.07257 52.5557 14.8443 52.5557 27.9988Z";

  /**
   * Generates a mathematically perfect sine wave path.
   * To ensure a seamless loop:
   * 1. The path length is a multiple of the wavelength.
   * 2. The frequency is calculated from the wavelength.
   */
  const createWavePath = () => {
    const wavelength = 140; // Total horizontal distance for one full cycle
    const amplitude = 6;
    const frequency = (2 * Math.PI) / wavelength;
    const height = 28; // Vertical baseline for the wave

    // Start from -wavelength to have enough buffer for horizontal animation
    let path = `M -${wavelength} ${height}`;

    // Generate points for 3 full wavelengths to ensure coverage during animation
    for (let x = -wavelength; x <= wavelength * 2; x += 2) {
      const y = height + Math.sin(x * frequency) * amplitude;
      path += ` L ${x} ${y}`;
    }

    path += `
      V 70
      H -${wavelength}
      Z
    `;

    return path;
  };

  const wavyPath = createWavePath();

  return (
    <div className={containerClasses}>
      <svg
        viewBox="0 0 56 56"
        className={svgClasses}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <mask id="logo-mask">
            <rect width="56" height="56" fill="black" />
            <path
              d={logoPath}
              fill="white"
              fillRule="evenodd"
              clipRule="evenodd"
            />
          </mask>
        </defs>

        {/* Base logo (Static Background) */}
        <path
          d={logoPath}
          className={styles.baseColor}
          fillRule="evenodd"
          clipRule="evenodd"
        />

        {/* Light wave layer */}
        <g mask="url(#logo-mask)">
          <g className={`${styles.waveLayer} ${styles.waveLayerLight}`}>
            <path
              d={wavyPath}
              className={`${styles.lightColor} ${styles.wavePath}`}
              style={{ transform: 'translateY(-6px)' }}
            />
          </g>
        </g>

        {/* Original wave layer (Successive fill) */}
        <g mask="url(#logo-mask)">
          <g className={`${styles.waveLayer} ${styles.waveLayerOriginal}`}>
            <path
              d={wavyPath}
              className={`${styles.baseColor} ${styles.wavePath}`}
              style={{ transform: 'translateY(-6px)' }}
            />
          </g>
        </g>
      </svg>
    </div>
  );
};
